import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Pagination } from 'components/Pagination/Pagination'
import { clearCapabilities, deleteCapability, getCapabilities } from 'features/capabilities/capabilitiesSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex, H1WithBack, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'

import { tokens } from 'styling/tokens'
import { ICapability } from 'types/api/capabilitiesApi.interface'
import { ModalTypes } from 'types/global.interface'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { CapabilitiesModal } from './CapabilitiesModal'

export const Capabilities = () => {
    const { data, totalPages } = useSelector((state: RootState) => state.capabilities)
    const dispatch = useDispatch<AppDispatch>()
    const [params, setParams] = useState<any>(getParams({ location, ap: { sd: 'a', s: 'name' } }))
    const [modal, setModal] = useState<{ open: boolean, value: ICapability }>({ open: false, value: null })

    useQueryPagination({ fetchFunctions: [getCapabilities], cleanUpFunctions: [clearCapabilities], params })

    const handleRemove = (id: number) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(deleteCapability(id))
                },
                setParams: {
                    type: ModalTypes.Delete,
                    f: setParams,
                },
                word: 'delete capability',
            }),
        )
    }

    const columns: IHonestTableColumn<any>[] = [
        {
            id: 'name',
            header: 'Name',
            cell: (item) => item.name,
        },
        {
            id: 'code',
            header: 'Code',
            cell: (item) => item.code || '/',
        },
        {
            id: 'requiresExpiry',
            header: 'Expiry',
            cell: (item) => (
                <StatusPill status={item.requiresExpiry ? 'accent' : 'neutral'}>
                    {item.requiresExpiry ? 'Required' : 'Not Required'}
                </StatusPill>
            ),
        },
        {
            id: 'requiresDocument',
            header: 'Document',
            cell: (item) => (
                <StatusPill status={item.requiresDocument ? 'accent' : 'neutral'}>
                    {item.requiresDocument ? 'Required' : 'Not Required'}
                </StatusPill>
            ),
        },
        {
            id: 'createdAt',
            header: 'Created',
            cell: (item) => (item.createdAt ? formatDateForDisplay(item.createdAt) : '/'),
        },
    ]

    const menuOptions: IHonestTableMenuOption<any>[] = [
        { label: 'Edit', onClick: (item) => setModal({ open: true, value: item }) },
        { label: 'Delete', color: tokens.status.danger.fg, onClick: (item) => handleRemove(item.id) },
    ]

    return <>
        <NavBar
            input
            defaultValue={params.q}
            placeholder='Search Capabilities...'
            setParams={setParams}
            searchFields={['name']}
        />
        <PageWrapper>
            <Flex between center mb='16' >
                <H1WithBack title='Capabilities' arrow />
                <Button variant='primary' onClick={() => setModal({ open: true, value: null })}>
                    Add Group
                </Button>
            </Flex>
            {data?.length ? (
                <HonestTable
                    columns={columns}
                    data={data as ICapability[]}
                    keyField='id'
                    onClick={(item) => setModal({ open: true, value: item })}
                    menuOptions={menuOptions}
                />
            ) : <NoData />}
        </PageWrapper>
        <PageFooter>
            <Pagination
                setPage={(page) => handlePagination(page, setParams)}
                pageCount={Math.ceil(totalPages)}
                forcePage={(params.page ? params.page : 1) - 1}
            />
        </PageFooter>
        {modal.open && <CapabilitiesModal value={modal.value} closeModal={() => setModal({ open: false, value: null })} />}
    </>
}
